import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    LinearProgress,
    Link,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { Form, Formik } from "formik";
import { DatePicker } from '@mui/x-date-pickers';
import { useDropzone, FileRejection } from 'react-dropzone';
import Swal from 'sweetalert2';
import useAuth from '../../context/AuthProvider';
import moment from 'moment';
import { formatUtcToLocal } from '../../helper/utils';
import { StateReportModel } from '../../models/StateReportModel';
import { FormLabel } from 'react-bootstrap';
import { shouldShowFiledNotesAttachments } from "../../helper/noClientActionRequired";

const DOCUMENT_DROPZONE_ACCEPT = {
    'application/pdf': ['.pdf'],
    'image/*': [] as string[],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/msword': ['.doc'],
};

const EXCEL_DROPZONE_ACCEPT = {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-excel': ['.xls'],
};

type AttachmentSlot = { id: string; file: File | null };

/** Same dashed dropzone look as File Now / Add-Edit State Report. */
function FiledNotesFileDropzone({
    label,
    acceptedFile,
    onFileDrop,
}: {
    label: string;
    acceptedFile: File | null;
    onFileDrop: (file: File | null) => void;
}) {
    const [isHovered, setIsHovered] = useState(false);

    const onDropRejected = (fileRejections: FileRejection[]) => {
        const first = fileRejections?.[0];
        const errCodes = first?.errors?.map((e) => e.code) || [];
        if (errCodes.includes('file-too-large')) {
            Swal.fire({
                icon: 'error',
                title: 'File too large',
                text: 'Maximum file size 10MB.',
                confirmButtonText: 'OK',
            });
            return;
        }
        if (errCodes.includes('file-invalid-type')) {
            Swal.fire({
                icon: 'error',
                title: 'Invalid file type',
                text: 'Only .pdf, Word, image, and Excel (.xlsx) files are allowed.',
                confirmButtonText: 'OK',
            });
        }
    };

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        maxFiles: 1,
        maxSize: 10 * 1024 * 1024,
        accept: { ...DOCUMENT_DROPZONE_ACCEPT, ...EXCEL_DROPZONE_ACCEPT },
        onDrop: (files) => {
            const file = files?.[0];
            if (file) onFileDrop(file);
        },
        onDropRejected,
    });

    const style = useMemo(
        () =>
            ({
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                borderWidth: 2,
                borderRadius: 8,
                borderColor: isDragReject ? '#ff1744' : isDragAccept ? '#00e676' : isFocused ? '#2196f3' : '#B0BEC5',
                borderStyle: 'dashed',
                backgroundColor: '#ECEFF1',
                color: '#546E7A',
                outline: 'none',
                transition: 'border .24s ease-in-out',
                minHeight: '120px',
                cursor: 'pointer',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }) as React.CSSProperties,
        [isFocused, isDragAccept, isDragReject],
    );

    const rootProps = getRootProps({ style });

    return (
        <Box
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            sx={{ position: 'relative', width: '100%' }}
        >
            <Box {...rootProps}>
                <input {...getInputProps()} />
                {acceptedFile ? (
                    <Box sx={{ textAlign: 'center', width: '100%', px: 1, overflow: 'hidden' }}>
                        <Typography
                            sx={{
                                fontWeight: 'bold',
                                color: '#1976D2',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                            title={acceptedFile.name}
                        >
                            {acceptedFile.name}
                        </Typography>
                        <Typography sx={{ mt: 0.5, fontSize: '12px', color: '#666' }}>
                            {(acceptedFile.size / 1024).toFixed(2)} KB
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center' }}>
                        <p style={{ margin: 0 }}>Click or drag file here</p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>{label}</p>
                    </Box>
                )}
            </Box>
            {!acceptedFile && isHovered && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '8px',
                            zIndex: 2,
                            cursor: 'pointer',
                        }}
                        onClick={(rootProps as { onClick?: (e: React.MouseEvent) => void }).onClick}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 3,
                            display: 'flex',
                            cursor: 'pointer',
                        }}
                        onClick={(rootProps as { onClick?: (e: React.MouseEvent) => void }).onClick}
                    >
                        <CloudUploadIcon
                            sx={{
                                color: '#ffffff',
                                fontSize: '32px',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            }}
                        />
                    </Box>
                </>
            )}
        </Box>
    );
}

export type FiledNotesSubmitData = {
    status: string;
    noteType: string;
    noteDetail: string;
    noteUser: string;
    firstRequestDate: string;
    secondRequestDate: string;
    thirdRequestDate: string;
    suspendedDate: string;
    noteDate?: string;
    policySubStatus: string;
    dateDeclarationFiled: string;
    dateFinalReportSentToClient: string;
    taxAmount: string;
    largeReport: boolean;
    largeReportFee: string;
    /** Optional files for No Client Action Required Filed Notes only. */
    attachments?: File[];
};

interface Iprops {
    handleClose: () => void;
    open: boolean;
    currentStatus?: string;
    /**
     * Persist notes / NCA complete. May be async.
     * Return `true` (or void) on success so the dialog can close after the API finishes;
     * return `false` to keep the dialog open (e.g. API error already shown).
     */
    handleSubmit: (data: FiledNotesSubmitData) => void | boolean | Promise<void | boolean>;
    selectedRows?: StateReportModel[] | null;
    /** Schedule mapping ActionType; when `no_action`, use the Filed Notes layout. */
    mappingActionType?: string;
}

// Pairs [stateName, name] that should set openFiledNotes true when matched
const FILED_NOTES_STATE_NAME_PAIRS: [string, string][] = [
    ['CO', 'Monthly Report'],
    ['KY', 'Annual Municipal Reconciliation (Electronic)'],
    ['KY', 'Annual Municipal Reconciliation (Individual Municipalities)'],
    ['MD', 'Quarterly Surplus Lines Tax Report'],
    ['MO', 'Quarterly Tax Report (Appendix 1)'],
    ['MO', 'Annual Tax Report (Appendix 3)'],
    ['MS', 'Monthly Certification Report'],
    ['PA', 'Monthly 1620 Report'],
];

const StateReportFilerNotes = ({ open, handleClose, handleSubmit, selectedRows = [], currentStatus, mappingActionType }: Iprops) => {
    const { user } = useAuth();
    // Status being applied (chosen from the Status menu), NOT the row's existing status.
    // Layout + Submit validation must key off the target status, otherwise changing a
    // completed row (e.g. Filed) to Waiting on Client / No Report Required validates
    // against the old status and the Submit button can never enable.
    const targetStatus = (currentStatus && currentStatus.trim())
        ? currentStatus.trim()
        : (selectedRows?.[0]?.status ?? '');

    // Filed Notes layout: schedule mapping no_action, or legacy state/name pairs.
    const [openFiledNotes, setOpenFiledNotes] = useState(false);
    useEffect(() => {
        const row = selectedRows?.[0];
        if (!row) {
            setOpenFiledNotes(false);
            return;
        }
        const isNoActionMapping =
            String(mappingActionType ?? '').trim().toLowerCase() === 'no_action';
        const rowStateName = (row as any).stateName ?? '';
        const rowName = (row as any).name ?? '';
        const matchesPair = FILED_NOTES_STATE_NAME_PAIRS.some(([s, n]) => s === rowStateName && n === rowName);
        setOpenFiledNotes(isNoActionMapping || matchesPair);
    }, [mappingActionType, selectedRows]);

    // State variables
    const [noteDetail, setNoteDetail] = useState<string>('');
    const [noteDetailFiled, setNoteDetailFiled] = useState<string>('');
    const [taxAmount, setTaxAmount] = useState<string>('');
    const [largeReport, setLargeReport] = useState(false);
    const [largeReportFee, setLargeReportFee] = useState<string>('');
    const [noteDate, setNoteDate] = useState<string>('');
    const [firstRequestDate, setFirstRequestDate] = useState('');
    // Reset form state when dialog opens for a new transaction
    useEffect(() => {
        if (open) {
            setLargeReportFee('');
            setLargeReport(false);
            setAttachmentSlots([{ id: '1', file: null }]);
        }
    }, [open]);
    const [secondRequestDate, setSecondRequestDate] = useState('');
    const [thirdRequestDate, setThirdRequestDate] = useState('');
    const [suspendedDate, setSuspendedDate] = useState('');
    const [showNoteDateField, setShowNoteDateField] = useState(false);
    const [dateComplete, setDateComplete] = useState('');
    const [dateCompleteError, setDateCompleteError] = useState('');
    const [attachmentSlots, setAttachmentSlots] = useState<AttachmentSlot[]>([
        { id: '1', file: null },
    ]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const showAttachments = shouldShowFiledNotesAttachments(openFiledNotes, targetStatus);

    const initialValuesNote = { noteOption: 'Select One', noteDetail: '' };

    // Prefill Tax Amount from schedule / row when Filed Notes opens.
    useEffect(() => {
        if (!open || !openFiledNotes) return;
        const prefill = String(selectedRows?.[0]?.taxAmount ?? '').trim();
        if (prefill !== '') {
            setNoteDetailFiled(prefill);
            setTaxAmount(prefill);
        }
    }, [open, openFiledNotes, selectedRows]);

    // Utility functions
    const shouldDisableDate = (date: Date) => {
        const minDate = new Date('1900-01-01');
        const maxDate = new Date();
        return date < minDate || date > maxDate;
    };

    const handleNoteDetailChangeFiled = (event: any) => {
        const update = event.target.value;
        setNoteDetailFiled(update);
        setTaxAmount(update);
    };

    const handleNote = (event: any) => {
        const update = event.target.value;
        setNoteDetail(update);
    };

    const handleNoteDetailChange = (event: any) => {
        const update = event.target.value;
        setNoteDetail(update);
        setShowNoteDateField(update !== '');
        setNoteDate(update === '' ? formatUtcToLocal(new Date().toISOString(), 'MM/DD/YYYY') : noteDate);
    };

    const handleNoteDate = (event: any) => {
        setNoteDate(event);
    };

    // Date handlers
    const handleDateComplete = (val: string | undefined) => {
        if (!val) {
            setDateComplete('');
            setDateCompleteError('');
            return;
        }

        const formattedInputDate = formatUtcToLocal(val, "MM/DD/YYYY");
        const todayInUiTimezone = formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY");

        if (formattedInputDate === "N/A") {
            setDateComplete('');
            setDateCompleteError('Enter date in MM/DD/YYYY format');
            return;
        }

        const inputDate = moment(formattedInputDate, "MM/DD/YYYY", true);
        const todayDate = moment(todayInUiTimezone, "MM/DD/YYYY", true);
        if (!inputDate.isValid() || !todayDate.isValid()) {
            setDateComplete('');
            setDateCompleteError('Enter date in MM/DD/YYYY format');
            return;
        }

        if (inputDate.isAfter(todayDate, 'day')) {
            setDateComplete('');
            setDateCompleteError('Please enter a date up to today');
            return;
        }

        setDateComplete(formattedInputDate);
        setDateCompleteError('');
    };

    const handleFirstRequestChange = (val: string | undefined) => {
        if (val != null) {
            setFirstRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleSecondRequestChange = (val: string | undefined) => {
        if (val != null) {
            setSecondRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleThirdRequestChange = (val: string | undefined) => {
        if (val != null) {
            setThirdRequestDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    const handleSuspendedRequestChange = (val: string | undefined) => {
        if (val != null) {
            setSuspendedDate(formatUtcToLocal(val, "MM/DD/YYYY"));
        }
    };

    // Reset form on close
    const handleBoxClose = () => {
        setNoteDetail('');
        setFirstRequestDate('');
        setSecondRequestDate('');
        setThirdRequestDate('');
        setNoteDate(formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"));
        setSuspendedDate('');
        setTaxAmount('');
        setNoteDetailFiled('');
        setDateComplete('');
        setDateCompleteError('');
        setLargeReport(false);
        setAttachmentSlots([{ id: '1', file: null }]);
        setIsSubmitting(false);
        handleClose();
    };

    const setAttachmentFile = (index: number, file: File | null) => {
        setAttachmentSlots((prev) => {
            const next = [...prev];
            if (!next[index]) return prev;
            next[index] = { ...next[index], file };
            return next;
        });
    };

    const removeAttachmentSlot = (index: number) => {
        setAttachmentSlots((prev) => {
            const next = prev.filter((_, i) => i !== index);
            return next.length > 0 ? next : [{ id: String(Date.now()), file: null }];
        });
    };

    const addAttachmentSlot = () => {
        setAttachmentSlots((prev) => {
            if (prev.length >= 12) return prev;
            return [...prev, { id: String(Date.now()), file: null }];
        });
    };

    // Reset form when selectedRows changes
    useEffect(() => {
        if (!selectedRows || selectedRows.length !== 1) {
            setNoteDetail('');
            setFirstRequestDate('');
            setSecondRequestDate('');
            setThirdRequestDate('');
            setNoteDate(formatUtcToLocal(new Date().toISOString(), "MM/DD/YYYY"));
            setSuspendedDate('');
        }
    }, [selectedRows]);

    return (
        <Dialog
            open={open}
            onClose={(_event, reason) => {
                if (isSubmitting) return;
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    handleBoxClose();
                    return;
                }
                handleBoxClose();
            }}
            maxWidth={'lg'}
        >
            <DialogTitle className='card-title'>
                {(openFiledNotes) ? "Filed Notes" : "Filer Notes"}
            </DialogTitle>
            <React.Fragment>
                <Formik
                    initialValues={initialValuesNote}
                    onSubmit={async () => {
                        if (isSubmitting) return;
                        setIsSubmitting(true);
                        try {
                            const result = await Promise.resolve(
                                handleSubmit({
                                    status: currentStatus ?? '',
                                    noteDetail: noteDetail,
                                    noteType: 'Select One',
                                    noteUser: user?.userFirstName ?? '',
                                    firstRequestDate: firstRequestDate !== "Invalid date" ? firstRequestDate : '',
                                    secondRequestDate,
                                    thirdRequestDate,
                                    suspendedDate,
                                    noteDate: noteDate !== "Invalid date" ? noteDate : '',
                                    policySubStatus: '',
                                    dateDeclarationFiled: '',
                                    dateFinalReportSentToClient: dateComplete ?? '',
                                    taxAmount: taxAmount ?? '',
                                    largeReport: largeReport,
                                    largeReportFee: largeReportFee ?? '',
                                    attachments: showAttachments
                                        ? attachmentSlots
                                              .map((slot) => slot.file)
                                              .filter((file): file is File => file != null)
                                        : [],
                                }),
                            );
                            // Keep dialog open when parent explicitly reports failure.
                            if (result === false) {
                                return;
                            }
                            handleBoxClose();
                        } catch {
                            // Parent already showed error Swal; keep dialog open.
                        } finally {
                            setIsSubmitting(false);
                        }
                    }}
                >
                    {({ values }) => {
                        return (
                            <Form className='main-form'>
                                {isSubmitting && (
                                    <LinearProgress color="info" sx={{ backgroundColor: '#132D3D' }} />
                                )}
                                <DialogContent>
                                    {(targetStatus === "No Report Required" || openFiledNotes) ? (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            disableFuture
                                                            minDate={new Date('1900-01-01')}
                                                            maxDate={new Date()}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Date Completed"
                                                            className="select-input-border text-input-box"
                                                            value={dateComplete ?? ''}
                                                            onChange={(v) => handleDateComplete(v?.toString())}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="dateComplete"
                                                                    name="dateComplete"
                                                                    error={!!dateCompleteError || !dateComplete}
                                                                    helperText={
                                                                        dateCompleteError
                                                                            ? dateCompleteError
                                                                            : !dateComplete
                                                                            ? 'Date Completed is required'
                                                                            : ''
                                                                    }
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            {(openFiledNotes) && (
                                                <>
                                                    {/* Tax Amount Field */}
                                                    <Grid item xs={12}>
                                                        <FormControl sx={{ width: '100%', minWidth: '400px' }}>
                                                            <TextField
                                                                label="Tax Amount"
                                                                placeholder="Enter '0' for Zero Reports"
                                                                type="number"
                                                                fullWidth
                                                                variant="outlined"
                                                                sx={{ marginTop: 2 }}
                                                                value={noteDetailFiled}
                                                                onChange={(e) => {
                                                                    const value = e.target.value;
                                                                    if (/^\d*\.?\d*$/.test(value)) {
                                                                        handleNoteDetailChangeFiled(e);
                                                                    }
                                                                }}
                                                                InputProps={{
                                                                    inputProps: { min: 0, step: "0.01" }
                                                                }}
                                                                error={!noteDetailFiled && openFiledNotes}
                                                                helperText={!noteDetailFiled && openFiledNotes ? 'This field is required' : ''}
                                                            />
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Large Report Field */}
                                                    <Grid item xs={12}>
                                                        <FormControl sx={{ width: '100%', minWidth: '400px', marginTop: 2 }}>
                                                            <FormLabel>Large Report</FormLabel>
                                                            <RadioGroup
                                                                row
                                                                value={largeReport}
                                                                onChange={(e) => {
                                                                    setLargeReport(e.target.value === 'true');
                                                                    // Clear the fee when setting largeReport to false
                                                                    if (e.target.value === 'false') {
                                                                        setLargeReportFee('');
                                                                    }
                                                                }}
                                                            >
                                                                <FormControlLabel value="true" control={<Radio />} label="True" />
                                                                <FormControlLabel value="false" control={<Radio />} label="False" />
                                                            </RadioGroup>
                                                            <FormHelperText error={largeReport === null && (openFiledNotes)}>
                                                                {largeReport === null && (openFiledNotes) ? 'This field is required' : ''}
                                                            </FormHelperText>
                                                        </FormControl>
                                                    </Grid>

                                                    {/* Large Report Fee Field - only show when Large Report is true */}
                                                    {largeReport && (
                                                        <Grid item xs={12}>
                                                            <FormControl sx={{ width: '100%', minWidth: '400px', marginTop: 2 }}>
                                                                <TextField
                                                                    label="Large Report Fee"
                                                                    type="number"
                                                                    value={largeReportFee}
                                                                    onChange={(e) => setLargeReportFee(e.target.value)}
                                                                    placeholder="Enter fee amount (e.g., 50.00)"
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    helperText="Enter the additional fee for large reports"
                                                                />
                                                            </FormControl>
                                                        </Grid>
                                                    )}

                                                    {showAttachments && (
                                                        <Grid item xs={12}>
                                                            <Box sx={{ mt: 2 }}>
                                                                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                                                                    Attachments (optional)
                                                                </Typography>
                                                                <Typography sx={{ fontSize: '0.875rem', color: '#64748b', mb: 1.5 }}>
                                                                    Please select a box to choose a file from your computer, or
                                                                    drag and drop your file into the appropriate box below.
                                                                    Attach a copy of the completed report or related documents for the client record.
                                                                </Typography>
                                                                <Grid container spacing={2}>
                                                                    {attachmentSlots.map((slot, index) => (
                                                                        <Grid item xs={12} sm={6} key={slot.id}>
                                                                            <Box sx={{ mb: 0.5, fontWeight: 'medium' }}>
                                                                                {index === 0
                                                                                    ? 'Completed Report / Attachment'
                                                                                    : `Supplemental Attachment ${index}`}
                                                                            </Box>
                                                                            <FiledNotesFileDropzone
                                                                                label={
                                                                                    index === 0
                                                                                        ? 'Completed Report / Attachment'
                                                                                        : `Supplemental Attachment ${index}`
                                                                                }
                                                                                acceptedFile={slot.file}
                                                                                onFileDrop={(file) => setAttachmentFile(index, file)}
                                                                            />
                                                                            {slot.file && (
                                                                                <Box sx={{ mt: 1, textAlign: 'center' }}>
                                                                                    <Link
                                                                                        component="button"
                                                                                        variant="body2"
                                                                                        type="button"
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            e.stopPropagation();
                                                                                            removeAttachmentSlot(index);
                                                                                        }}
                                                                                        sx={{ color: '#d32f2f', cursor: 'pointer' }}
                                                                                    >
                                                                                        Remove
                                                                                    </Link>
                                                                                </Box>
                                                                            )}
                                                                        </Grid>
                                                                    ))}
                                                                </Grid>
                                                                {attachmentSlots.length < 12 && (
                                                                    <Box sx={{ mt: 2 }}>
                                                                        <Button
                                                                            variant="outlined"
                                                                            type="button"
                                                                            startIcon={<AttachFileIcon />}
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                addAttachmentSlot();
                                                                            }}
                                                                            sx={{
                                                                                textTransform: 'none',
                                                                                fontWeight: 600,
                                                                                borderColor: '#1976D2',
                                                                                color: '#1976D2',
                                                                                '&:hover': {
                                                                                    borderColor: '#1565C0',
                                                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                                                },
                                                                            }}
                                                                        >
                                                                            Add More Supplemental Attachments
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </Grid>
                                                    )}
                                                </>
                                            )}

                                            <Grid item xs={12}>
                                                <FormControl sx={{ width: '100%', minWidth: '400px' }}>
                                                    <TextField
                                                        label="Note"
                                                        rows={3}
                                                        type="text"
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{ marginTop: 2 }}
                                                        multiline
                                                        value={noteDetail}
                                                        onChange={handleNote}
                                                        required={targetStatus === "No Report Required"}
                                                        error={!noteDetail && targetStatus === "No Report Required"}
                                                        helperText={
                                                            !noteDetail && targetStatus === "No Report Required"
                                                                ? 'Note is required'
                                                                : ''
                                                        }
                                                    />
                                                </FormControl>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <FormControl sx={{ width: '100%', minWidth: '500px' }}>
                                                    <FormHelperText className='formtexthelper-heading'>
                                                        Note
                                                    </FormHelperText>
                                                    <TextField
                                                        label="Note"
                                                        rows={3}
                                                        type="text"
                                                        fullWidth
                                                        variant="outlined"
                                                        sx={{ marginTop: 2 }}
                                                        multiline
                                                        value={noteDetail}
                                                        onChange={(e) => handleNoteDetailChange(e)}
                                                        required
                                                        error={!noteDetail.trim()}
                                                        helperText={!noteDetail.trim() ? 'Note is required' : ''}
                                                    />
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sx={{ marginTop: 2 }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="First Request"
                                                            className="select-input-border text-input-box"
                                                            value={firstRequestDate ?? ''}
                                                            onChange={(v) => handleFirstRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="firstRequestDate"
                                                                    name="firstRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Second Request"
                                                            className="select-input-border text-input-box"
                                                            value={secondRequestDate ?? ''}
                                                            onChange={(v) => handleSecondRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="secondRequestDate"
                                                                    name="secondRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={3}>
                                                        <DatePicker
                                                            shouldDisableDate={shouldDisableDate}
                                                            inputFormat="MM/DD/YYYY"
                                                            label="Third & Final Request"
                                                            className="select-input-border text-input-box"
                                                            value={thirdRequestDate ?? ''}
                                                            onChange={(v) => handleThirdRequestChange(v?.toString())}
                                                            renderInput={(params: any) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="thirdRequestDate"
                                                                    name="thirdRequestDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={.3}></Grid>
                                                {showNoteDateField && (
                                                    <Grid item xl={3} lg={3} md={6} sm={12} xs={12} marginTop={3}>
                                                        <FormHelperText className='formtexthelper-heading'>Note Date</FormHelperText>
                                                        <DatePicker
                                                            inputFormat="MM/DD/YYYY"
                                                            label={null}
                                                            className="select-input-border text-input-box"
                                                            shouldDisableDate={shouldDisableDate}
                                                            value={noteDate}
                                                            onChange={handleNoteDate}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    {...params}
                                                                    id="effectiveDateStart"
                                                                    name="policyEffDate"
                                                                />
                                                            )}
                                                        />
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Grid>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <button
                                        className='btn-2 btn d-flex btn-primary search-btn'
                                        type='button'
                                        onClick={handleBoxClose}
                                        disabled={isSubmitting}
                                    >
                                        Cancel
                                    </button>
                                    {(() => {
                                        const hasPermission = user?.userRoles.split(",").includes("Super Admin") ||
                                            user?.userRoles.split(",").includes("MISC Filer") ||
                                            user?.userRoles.split(",").includes("MISC State Reporter");

                                        if (!hasPermission) {
                                            return (
                                                <button
                                                    className='btn btn-primary active-btn-blue search-btn'
                                                    disabled
                                                >
                                                    Submit
                                                </button>
                                            );
                                        }

                                        let isValidForSubmit = false;

                                        if (openFiledNotes) {
                                            isValidForSubmit = !!(dateComplete && noteDetailFiled.trim());
                                        } else {
                                            switch (targetStatus) {
                                                case "Filed":
                                                    isValidForSubmit = !!(dateComplete && noteDetailFiled);
                                                    break;
                                                case "No Report Required":
                                                    isValidForSubmit = !!(dateComplete && noteDetail.trim());
                                                    break;
                                                case "Waiting on Client":
                                                    isValidForSubmit = !!noteDetail.trim();
                                                    break;
                                                default:
                                                    isValidForSubmit = !!noteDetail.trim();
                                                    break;
                                            }
                                        }

                                        return (
                                            <button
                                                className='btn btn-primary active-btn-blue search-btn'
                                                type='submit'
                                                disabled={!isValidForSubmit || isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                                        <CircularProgress size={16} sx={{ color: '#fff' }} />
                                                        Saving…
                                                    </span>
                                                ) : (
                                                    'Submit'
                                                )}
                                            </button>
                                        );
                                    })()}
                                </DialogActions>
                            </Form>
                        );
                    }}
                </Formik>
            </React.Fragment>
        </Dialog>
    );
};

export default StateReportFilerNotes;
